import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { CustomButton, CustomMRTTable } from "../../../components";
import AxiosInstance from "../../../services/Api";
import styles from "../plans-pricing/PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/slab-configuration";
const PLANS_PATH = "/dashboard/masters/plans-pricing";
const SLAB_LIST_API = "/saas/list/slab-configuration";

// ── Status mapping — API: 1 = Active, 0 = Inactive ───────────────────────────
// Any unrecognised value (e.g. legacy 3) renders a grey "Unknown" badge
const STATUS_MAP = {
  1: { label: "Active", style: { backgroundColor: "#00A63E", color: "#fff" } },
  0: { label: "Inactive", style: { backgroundColor: "#E7000B", color: "#fff" } },
};

const StatusBadge = ({ statusCode }) => {
  // ✅ Number() coercion — handles string "0"/"1" from API too
  const entry = STATUS_MAP[Number(statusCode)];
  const { label, style } = entry || {
    label: "Unknown",
    style: { backgroundColor: "#9e9e9e", color: "#fff" },
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 14px",
        borderRadius: "6px",
        fontSize: "13px",
        fontWeight: 600,
        ...style,
      }}
    >
      {label}
    </span>
  );
};

const SlabConfigList = () => {
  const navigate = useNavigate();

  const [slabList, setSlabList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // ✅ No "Upcoming" (status 2) in real API data — Create always enabled
  const hasUpcoming = false;

  // ── Fetch slab list ───────────────────────────────────────────────────────
  const fetchSlabList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        status: "",
      };
      const response = await AxiosInstance.post(SLAB_LIST_API, payload);

      if (response.data?.status && response.data.data) {
        setSlabList(response.data.data.list || []);
        setTotalRecords(response.data.data.pagination?.total || 0);
      } else {
        setSlabList([]);
        setTotalRecords(0);
        throw new Error(
          response.data?.message || "Failed to fetch slab configuration list",
        );
      }
    } catch (err) {
      console.error("Error fetching slab configuration list:", err);
      setError(err.message || "An error occurred while fetching slab configurations");
      setSlabList([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => { fetchSlabList(); }, [fetchSlabList]);

  const handleEditClick = (row) => {
    navigate(`${LIST_PATH}/create`, {
      state: { slabId: row.original.id },
    });
  };

  // ── Dynamic range columns from first row ──────────────────────────────────
  const rangeColumns = useMemo(() => {
    const sampleRow = slabList[0];
    if (!sampleRow) return [];

    const rangeKeys = Object.keys(sampleRow)
      .filter((key) => /^range\d+$/i.test(key))
      .sort((a, b) =>
        parseInt(a.replace(/\D/g, ""), 10) - parseInt(b.replace(/\D/g, ""), 10),
      );
    return rangeKeys.map((key) => ({
      accessorKey: key,
      header: `Range ${key.replace(/\D/g, "")}`,
      size: 130,
    }));
  }, [slabList]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "s_no",
        header: "S.No",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      ...rangeColumns,
      {
        accessorKey: "is_addon",
        header: "Is Add-on Created ?",
        size: 170,
        enableColumnFilter: false,
        // ✅ Coerce to Number — API may return 0/1 or "0"/"1"
        Cell: ({ cell }) => (Number(cell.getValue()) === 1 ? "Yes" : "No"),
      },
      {
        accessorKey: "is_plan_created",
        header: "Is Plan Created ?",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => (Number(cell.getValue()) === 1 ? "Yes" : "No"),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 140,
        enableColumnFilter: false,
        // ✅ StatusBadge handles Number coercion internally
        Cell: ({ cell }) => <StatusBadge statusCode={cell.getValue()} />,
      },
      {
        id: "actions",
        header: "Action",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              className={styles.iconActionBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [pagination.pageIndex, pagination.pageSize, rangeColumns],
  );

  const columnOrder = useMemo(
    () => columns.map((col) => col.id || col.accessorKey),
    [columns],
  );

  return (
    <div>    
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Slab Configuration</h1>
        </div>
        <div className={styles.headerActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={() => navigate(`${LIST_PATH}/create`)}
          >
            Create Slab
          </CustomButton>
        </div>
      </div>

      {error && (
        <p style={{ color: "#E23B3B", margin: "12px 0" }}>{error}</p>
      )}

      <div className={styles.card}>
        <CustomMRTTable
          columns={columns}
          data={slabList}
          rowCount={totalRecords}
          isLoading={loading}
          columnOrder={columnOrder}
          manualPagination
          manualFiltering={false}
          pagination={pagination}
          onPaginationChange={setPagination}
          enablePagination
          enableSorting
          enableSearch={false}
          enableColumnFilters={false}
          enableColumnVisibility={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          enableColumnActions={false}
          enableStickyHeader
        />
      </div>
    </div>
  );
};

export default SlabConfigList;
